import React from "react";
import { Form, Col, Row } from "react-bootstrap";
import { NumericFormat as NumberFormat, PatternFormat } from "react-number-format";

import { ConnectedSettingList } from "../../layout/select/setting-list";
import Address from "../../layout/address";
import ConditionalButton from "../../layout/conditional-button";
import { deepEqual } from "../../utils/object";

class FirmSettings extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            firm: props.firm || {},
            addresses: props.addresses || []
        };
    }

    componentDidUpdate = (prevProps) => {
        // Only sync local state when incoming props truly changed
        if (
            deepEqual(this.props.firm, prevProps.firm) &&
            deepEqual(this.props.addresses, prevProps.addresses)
        ) {
            return;
        }

        this.setState({
            firm: this.props.firm || {},
            addresses: this.props.addresses || []
        });
    };

    onChange = (e) => {
        const { name, value } = e.target || {};

        // Optimistic local update for controlled inputs
        if (name) {
            this.setState((prev) => ({
                firm: {
                    ...(prev.firm || {}),
                    [name]: value
                }
            }));
        }

        if (this.props.onChange) this.props.onChange(e);
    };

    onAddAddressClick = (e) => {
        if (this.props.onAddAddressClick) this.props.onAddAddressClick(e);
    };

    onAddressChange = (address, index) => {
        // Optimistic local update so Address edits feel immediate
        this.setState((prev) => ({
            addresses: (prev.addresses || []).map((item, i) =>
                i === index ? { ...address } : item
            )
        }));

        if (this.props.onAddressChange) this.props.onAddressChange(address, index);
    };

    onAddressDelete = (address, index) => {
        if (this.props.onAddressDelete) this.props.onAddressDelete(address, index);
    };

    render() {
        const firm = this.state.firm || {};
        const addresses = this.state.addresses || [];

        return (
            <>
                <Row className="g-3">
                    <Col md="7" lg="8">
                        <div className="mb-3 top-label">
                            <Form.Control
                                type="text"
                                name="FIRM_LEGAL_NAME"
                                value={firm["FIRM_LEGAL_NAME"] ?? ""}
                                onChange={this.onChange}
                            />
                            <Form.Label>LEGAL NAME</Form.Label>
                        </div>
                    </Col>

                    <Col md="2">
                        <div className="mb-3 top-label">
                            <Form.Control
                                type="text"
                                name="FIRM_ALIAS"
                                value={firm["FIRM_ALIAS"] ?? ""}
                                onChange={this.onChange}
                            />
                            <Form.Label>ALIAS</Form.Label>
                        </div>
                    </Col>

                    <Col md="3" lg="2">
                        <div className="mb-3 top-label">
                            <PatternFormat
                                format="##-#######"
                                mask="_"
                                className="form-control"
                                allowEmptyFormatting
                                value={firm["FIRM_EIN"] ?? ""}
                                onValueChange={({ formattedValue, value }) => {
                                    this.onChange({
                                        target: {
                                            name: "FIRM_EIN",
                                            value: value.length ? formattedValue : ""
                                        }
                                    });
                                }}
                            />
                            <Form.Label>EIN</Form.Label>
                        </div>
                    </Col>
                </Row>

                <Row className="g-3">
                    <Col md="4">
                        <div className="mb-3 top-label">
                            <NumberFormat
                                name="FIRM_INITIAL_FEE"
                                id="FIRM_INITIAL_FEE"
                                value={firm["FIRM_INITIAL_FEE"] ?? ""}
                                thousandSeparator
                                allowNegative={false}
                                inputMode="decimal"
                                decimalScale={2}
                                className="text-end form-control mb-2"
                                isAllowed={({ floatValue, value }) =>
                                    value === "" || (floatValue ?? 0) <= 10000
                                }
                                onValueChange={({ floatValue }) => {
                                    this.onChange({
                                        target: {
                                            name: "FIRM_INITIAL_FEE",
                                            value: floatValue ?? null
                                        }
                                    });
                                }}
                            />
                            <Form.Label>CASE SETUP FEE</Form.Label>
                        </div>
                    </Col>

                    <Col md="4">
                        <div className="mb-3 top-label">
                            <NumberFormat
                                name="FIRM_CHECK_FEE"
                                id="FIRM_CHECK_FEE"
                                value={firm["FIRM_CHECK_FEE"] ?? ""}
                                thousandSeparator
                                allowNegative={false}
                                inputMode="decimal"
                                decimalScale={2}
                                className="text-end form-control mb-2"
                                isAllowed={({ floatValue, value }) =>
                                    value === "" || (floatValue ?? 0) <= 10000
                                }
                                onValueChange={({ floatValue }) => {
                                    this.onChange({
                                        target: {
                                            name: "FIRM_CHECK_FEE",
                                            value: floatValue ?? null
                                        }
                                    });
                                }}
                            />
                            <Form.Label>RETURNED CHECK FEE</Form.Label>
                        </div>
                    </Col>

                    <Col md="4">
                        <div className="mb-3 top-label">
                            <ConnectedSettingList
                                name="FIRM_CURRENCY"
                                setting="CURRENCIES"
                                category="Collections"
                                value={firm["FIRM_CURRENCY"] ?? ""}
                                onChange={this.onChange}
                            />
                            <Form.Label>DEFAULT CURRENCY</Form.Label>
                        </div>
                    </Col>
                </Row>

                <Row className="g-3">
                    <Col md="12">
                        <div className="mb-3 top-label">
                            <Form.Control
                                type="url"
                                name="FIRM_REVIEW_GOOGLE_URL"
                                value={firm["FIRM_REVIEW_GOOGLE_URL"] ?? ""}
                                onChange={this.onChange}
                                placeholder="https://g.page/r/..."
                            />
                            <Form.Label>GOOGLE REVIEW URL</Form.Label>
                        </div>
                    </Col>
                </Row>

                <Row className="g-3 mb-3">
                    <Col lg="3" className="pt-2">
                        <h2 className="small-title">Addresses</h2>
                    </Col>
                    <Col lg="9" className="float-right text-end mb-3">
                        <ConditionalButton
                            enabledVariant="outline-primary"
                            disabledVariant="muted"
                            tooltip="Add Address"
                            display
                            enabled
                            onEnabledClick={this.onAddAddressClick}
                            icon="plus"
                        >
                            Add
                        </ConditionalButton>
                    </Col>
                </Row>

                {addresses.map((item, index) => (
                    <Row
                        key={`address-row.${item?.uuid || index}`}
                        className="g-3 mb-3"
                        style={{ backgroundColor: index % 2 === 0 ? "#e9f8fbd3" : "white" }}
                    >
                        <Col md="9" lg="10" xxl="11">
                            <Address
                                {...item}
                                onChange={(e) => this.onAddressChange(e, index)}
                            />
                        </Col>
                        <Col md="3" lg="2" xxl="1" className="float-right text-end">
                            <ConditionalButton
                                enabledVariant="outline-primary"
                                disabledVariant="muted"
                                tooltip="Delete Address"
                                display
                                enabled
                                onEnabledClick={() => this.onAddressDelete(item, index)}
                                icon="bin"
                            >
                                Delete
                            </ConditionalButton>
                        </Col>
                    </Row>
                ))}
            </>
        );
    }
}

export default FirmSettings;
